import React, { useState, useEffect } from 'react';
import UserForm from './components/UserForm';
import UploadZone from './components/UploadZone';
import ThumbnailGallery from './components/ThumbnailGallery';
import PhotoEditor from './components/PhotoEditor';
import TermsModal from './components/TermsModal';
import UploadProgress from './components/UploadProgress';
import SuccessModal from './components/SuccessModal';
import FoldersView from './components/FoldersView';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import { createSession, uploadPhotos, listPhotos, deletePhoto, createOrder } from './api/api';
import { applyImageAdjustments } from './utils/imageUtils';
import './index.css';

function App() {
  const [step, setStep] = useState('form'); // form, upload, complete, folders
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [showTerms, setShowTerms] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(0);
  const [currentFileName, setCurrentFileName] = useState('');

  // Load session from localStorage on mount
  useEffect(() => {
    // Check if accessing admin panel
    if (window.location.pathname === '/admin') {
      const savedAdminUser = localStorage.getItem('adminUser');
      if (savedAdminUser) {
        try {
          setAdminUser(JSON.parse(savedAdminUser));
          setIsAdmin(true);
        } catch (error) {
          console.error('Error loading admin user:', error);
          localStorage.removeItem('adminUser');
        }
      } else {
        setIsAdmin(true);
      }
      return;
    }

    // Check for URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    const sessionParam = urlParams.get('session');

    if (view === 'folders') {
      setStep('folders');
      return;
    }

    if (sessionParam) {
      // Load specific session and override current session
      loadPhotos(sessionParam);
      const storedSession = localStorage.getItem(`session_${sessionParam}`);
      if (storedSession) {
        try {
          const data = JSON.parse(storedSession);
          
          // Override current session in localStorage
          localStorage.setItem('sessionId', sessionParam);
          localStorage.setItem('sessionData', JSON.stringify(data));
          
          setSessionData(data);
          setStep('upload');
          
          // Clear URL parameter after loading
          window.history.replaceState({}, '', window.location.pathname);
        } catch (error) {
          console.error('Error loading session:', error);
        }
      } else {
        // Session not found in localStorage, try to fetch from server
        console.warn('Session not found in localStorage, creating placeholder');
        const placeholderSession = {
          sessionId: sessionParam,
          name: 'Shared Session',
          formData: {
            name: 'Shared Session',
            quantity: 0,
            sizeId: null
          }
        };
        
        localStorage.setItem('sessionId', sessionParam);
        localStorage.setItem('sessionData', JSON.stringify(placeholderSession));
        localStorage.setItem(`session_${sessionParam}`, JSON.stringify(placeholderSession));
        
        setSessionData(placeholderSession);
        setStep('upload');
        
        // Clear URL parameter after loading
        window.history.replaceState({}, '', window.location.pathname);
      }
      return;
    }

    const savedSessionId = localStorage.getItem('sessionId');
    const savedSessionData = localStorage.getItem('sessionData');
    
    if (savedSessionId && savedSessionData) {
      try {
        const data = JSON.parse(savedSessionData);
        setSessionData(data);
        setStep('upload');
        loadPhotos(savedSessionId);
      } catch (error) {
        console.error('Error loading session:', error);
        localStorage.removeItem('sessionId');
        localStorage.removeItem('sessionData');
      }
    }
  }, []);

  const loadPhotos = async (sessionId) => {
    try {
      const response = await listPhotos(sessionId);
      setPhotos(response.photos || []);
    } catch (error) {
      console.error('Error loading photos:', error);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      const response = await createSession(formData.name);
      const session = {
        ...response.session,
        formData,
      };

      setSessionData(session);
      setOrderData(formData);
      localStorage.setItem('sessionId', session.sessionId);
      localStorage.setItem('sessionData', JSON.stringify(session));
      
      // Save to session history for folders view
      localStorage.setItem(`session_${session.sessionId}`, JSON.stringify(session));
      
      // Add to userSessions array if not already there
      const userSessions = JSON.parse(localStorage.getItem('userSessions') || '[]');
      const sessionExists = userSessions.some(s => s.sessionId === session.sessionId);
      if (!sessionExists) {
        userSessions.push({
          sessionId: session.sessionId,
          name: session.name || formData.name,
          createdAt: new Date().toISOString(),
          formData: formData
        });
        localStorage.setItem('userSessions', JSON.stringify(userSessions));
      }
      
      setStep('upload');
    } catch (error) {
      console.error('Error creating session:', error);
      alert('Failed to create session. Please try again.');
    }
  };

  const handleFilesSelected = async (files) => {
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleUploadClick = () => {
    if (selectedFiles.length === 0 && photos.length === 0) {
      alert('Please select at least one photo to upload');
      return;
    }
    setShowTerms(true);
  };

  const handleTermsAgree = async () => {
    setShowTerms(false);
    
    if (selectedFiles.length > 0) {
      await processAndUpload(selectedFiles);
    }
  };

  const processAndUpload = async (files) => {
    setIsUploading(true);
    setUploadProgress(0);
    setCurrentFileIndex(0);
    
    const startTime = Date.now();
    let totalBytesProcessed = 0;
    let uploadedCount = 0;

    try {
      // Upload files in parallel batches (NO COMPRESSION for full quality)
      const BATCH_SIZE = 5; // Upload 5 files simultaneously
      const batches = [];
      
      for (let i = 0; i < files.length; i += BATCH_SIZE) {
        batches.push(files.slice(i, i + BATCH_SIZE));
      }

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        // Upload all files in this batch simultaneously with retry logic
        await Promise.all(
          batch.map(async (file, indexInBatch) => {
            const overallIndex = batchIndex * BATCH_SIZE + indexInBatch;
            
            setCurrentFileIndex(overallIndex + 1);
            setCurrentFileName(file.name);
            
            // Retry logic for failed uploads
            let retries = 3;
            let uploaded = false;
            
            while (retries > 0 && !uploaded) {
              try {
                // Upload original file directly (no compression)
                await uploadPhotos(sessionData.sessionId, [file], () => {});
                uploaded = true;
                
                uploadedCount++;
                totalBytesProcessed += file.size;
                
                // Update progress
                const progress = Math.round((uploadedCount / files.length) * 100);
                setUploadProgress(progress);
                
                // Calculate speed and estimated time
                const elapsedSeconds = (Date.now() - startTime) / 1000;
                const speedMBps = (totalBytesProcessed / 1024 / 1024) / elapsedSeconds;
                setUploadSpeed(speedMBps);
                
                const remainingFiles = files.length - uploadedCount;
                const avgFileSize = totalBytesProcessed / uploadedCount;
                const remainingBytes = remainingFiles * avgFileSize;
                const estimatedSeconds = remainingBytes / (totalBytesProcessed / elapsedSeconds);
                setEstimatedTime(estimatedSeconds);
              } catch (error) {
                retries--;
                console.error(`Upload failed for ${file.name}, retries left: ${retries}`, error);
                if (retries > 0) {
                  // Wait 1 second before retry
                  await new Promise(resolve => setTimeout(resolve, 1000));
                } else {
                  throw new Error(`Failed to upload ${file.name} after 3 attempts`);
                }
              }
            }
          })
        );
      }

      const response = { success: true };

      // Create order
      if (orderData) {
        await createOrder(sessionData.sessionId, orderData.sizeId, orderData.quantity);
      }

      // Refresh photo list
      await loadPhotos(sessionData.sessionId);
      
      // Update session photo count in userSessions
      const userSessions = JSON.parse(localStorage.getItem('userSessions') || '[]');
      const sessionIndex = userSessions.findIndex(s => s.sessionId === sessionData.sessionId);
      if (sessionIndex !== -1) {
        userSessions[sessionIndex].photoCount = photos.length + files.length;
        localStorage.setItem('userSessions', JSON.stringify(userSessions));
      }
      
      // Update individual session data
      const sessionKey = `session_${sessionData.sessionId}`;
      const individualSession = JSON.parse(localStorage.getItem(sessionKey) || '{}');
      individualSession.photoCount = photos.length + files.length;
      localStorage.setItem(sessionKey, JSON.stringify(individualSession));
      
      setSelectedFiles([]);
      setIsUploading(false);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  const handleDeletePhoto = async (photo) => {
    try {
      await deletePhoto(photo.id);
      setPhotos(prev => prev.filter(p => p.id !== photo.id));
      setShowEditor(false); // Close editor after delete
      return true;
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo');
      return false;
    }
  };

  // Expose delete handler globally for PhotoEditor
  useEffect(() => {
    window.handlePhotoDelete = handleDeletePhoto;
    window.closeEditor = () => setShowEditor(false);
    return () => {
      delete window.handlePhotoDelete;
      delete window.closeEditor;
    };
  }, [photos]);

  const handleEditPhoto = (photo) => {
    setCurrentPhoto(photo);
    setShowEditor(true);
  };

  const handlePhotoClick = (photo) => {
    setCurrentPhoto(photo);
    setShowEditor(true);
  };

  const handleSaveEdit = async (photo, adjustments, adjustedImageBlob) => {
    try {
      console.log('Saving photo with adjustments:', { photo, adjustments });
      
      // Upload the edited blob to replace the original
      const { updatePhoto } = await import('./api/api');
      const response = await updatePhoto(photo.id, adjustedImageBlob);
      
      // Refresh photos to show updated version
      const photosResponse = await listPhotos(sessionData?.sessionId);
      setPhotos(photosResponse.photos || []);
      
      console.log('✅ Photo updated successfully:', response);
      // Don't close editor here, let PhotoEditor handle it
    } catch (error) {
      console.error('Error saving photo adjustments:', error);
      throw error;
    }
  };
  
  // Expose refresh function for components
  useEffect(() => {
    window.refreshPhotos = async () => {
      if (sessionData?.sessionId) {
        try {
          const response = await listPhotos(sessionData.sessionId);
          setPhotos(response.photos || []);
        } catch (error) {
          console.error('Error refreshing photos:', error);
        }
      }
    };
    return () => {
      delete window.refreshPhotos;
    };
  }, [sessionData]);

  const handleNewSession = () => {
    localStorage.removeItem('sessionId');
    localStorage.removeItem('sessionData');
    setSessionData(null);
    setPhotos([]);
    setSelectedFiles([]);
    setStep('form');
    setShowSuccess(false);
  };

  const handleAdminLogin = (admin) => {
    setAdminUser(admin);
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('adminUser');
    setAdminUser(null);
    window.location.href = '/';
  };

  // Admin Panel Rendering
  if (isAdmin) {
    if (!adminUser) {
      return <AdminLogin onLoginSuccess={handleAdminLogin} />;
    }
    return <AdminDashboard onLogout={handleAdminLogout} />;
  }

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(to bottom right, #FFE5F1, #E27C7A)'}}>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        {step === 'form' && (
          <div className="space-y-6">
            <div className="flex items-center justify-center min-h-[calc(100vh-250px)]">
              <UserForm 
                onSubmit={handleFormSubmit}
                onViewFolders={() => setStep('folders')}
              />
            </div>
          </div>
        )}

        {step === 'folders' && (
          <FoldersView onBack={() => setStep('form')} />
        )}

        {step === 'upload' && (
          <div className="space-y-8">
            {/* Session Info */}
            <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 md:p-6">
              <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3 md:gap-4">
                <div>
                  <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800">
                    Welcome, {sessionData?.name}!
                  </h2>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 mt-0.5 md:mt-1">
                    {sessionData?.formData?.quantity || 0} prints • {orderData?.sizeId ? 'Size selected' : 'No size'}
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                  <div className="text-right">
                    <p className="text-[10px] sm:text-xs md:text-sm text-gray-500">Photos</p>
                    <p className="text-lg sm:text-xl md:text-2xl font-bold text-primary-600">
                      {photos.length + selectedFiles.length}
                    </p>
                    {orderData?.quantity && (photos.length + selectedFiles.length) < orderData.quantity && (
                      <p className="text-[10px] sm:text-xs md:text-sm text-orange-600 font-semibold mt-0.5 md:mt-1">
                        ⚠️ {orderData.quantity - (photos.length + selectedFiles.length)} Remaining
                      </p>
                    )}
                    {orderData?.quantity && (photos.length + selectedFiles.length) > orderData.quantity && (
                      <p className="text-[10px] sm:text-xs md:text-sm text-red-600 font-semibold mt-0.5 md:mt-1">
                        ⚠️ {(photos.length + selectedFiles.length) - orderData.quantity} Extra
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Zone with Header */}
            <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4 md:mb-6">
                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800">Upload Photos</h3>
                <button
                  onClick={() => {
                    if (window.confirm('Go back to home? Any unsaved changes will be lost.')) {
                      handleNewSession();
                    }
                  }}
                  className="px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 text-xs sm:text-sm md:text-base text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                >
                  ← <span className="hidden xs:inline">Back to</span> Home
                </button>
              </div>
              <UploadZone
                onFilesSelected={handleFilesSelected}
                disabled={isUploading}
              />
            </div>

            {/* Selected Photos Preview (not yet uploaded) */}
            {selectedFiles.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 md:p-6 mb-20 md:mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm md:text-lg font-semibold text-gray-800">
                    📂 Selected Photos ({selectedFiles.length})
                  </h3>
                  <button
                    onClick={() => setSelectedFiles([])}
                    className="text-xs md:text-sm text-red-600 hover:text-red-700"
                  >
                    Clear All
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3 max-h-40 overflow-y-auto scrollbar-thin">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="relative bg-white rounded-lg p-1 md:p-2 shadow">
                      <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-xs text-gray-600 truncate mt-1">{file.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Thumbnail Gallery */}
            {photos.length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <ThumbnailGallery
                  photos={photos}
                  onDelete={handleDeletePhoto}
                  onEdit={handleEditPhoto}
                  onPhotoClick={handlePhotoClick}
                />
              </div>
            )}

            {/* Upload Button - Fixed on Mobile */}
            {(selectedFiles.length > 0 || photos.length > 0) && (
              <div className="fixed bottom-0 left-0 right-0 p-2 sm:p-3 md:p-4 bg-transparent md:relative z-30 space-y-2 sm:space-y-3">
                <button
                  onClick={handleUploadClick}
                  disabled={isUploading || selectedFiles.length === 0}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-2.5 sm:py-3 md:py-4 px-4 sm:px-6 md:px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 transform disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base md:text-lg"
                >
                  {selectedFiles.length > 0
                    ? `Upload ${selectedFiles.length} Photo(s) ⬆️`
                    : 'No New Photos to Upload'}
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      <TermsModal
        isOpen={showTerms}
        photoCount={selectedFiles.length}
        onAgree={handleTermsAgree}
        onCancel={() => setShowTerms(false)}
      />

      {showEditor && currentPhoto && (
        <PhotoEditor
          photo={currentPhoto}
          photos={photos}
          onClose={() => setShowEditor(false)}
          onSave={handleSaveEdit}
        />
      )}

      <UploadProgress
        isVisible={isUploading}
        progress={uploadProgress}
        currentFile={currentFileIndex}
        totalFiles={selectedFiles.length}
        fileName={currentFileName}
        uploadSpeed={uploadSpeed}
        estimatedTime={estimatedTime}
      />

      <SuccessModal
        isOpen={showSuccess}
        photoCount={photos.length}
        sessionId={sessionData?.sessionId}
        onClose={() => setShowSuccess(false)}
      />
    </div>
  );
}

export default App;

